import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SohoAccordionComponent, SohoDataGridComponent } from 'ids-enterprise-ng';
import { first } from 'rxjs/operators';
import { LMFacadeService } from '@app/components';
import { LMThemeService } from '@banner/lm-theme.service';

@Component({
  selector:    'lm-facade-module',
  standalone: false,
  templateUrl: './lm-facade-module.component.html',
  styleUrl: './lm-facade-module.component.scss'
})
export class LMFacadeModuleComponent implements OnInit, OnChanges, OnDestroy {

  @HostBinding('class.scrollable') scrollable = true;

  toggleSearchLabel = Soho.Locale.translate('ToggleSearch');
  facadeDetailRequest: LMFacadeDetailRequest | undefined;
  moduleGridOptions: SohoDataGridOptions | undefined;
  moduleGridData: Array<LMBusinessObject> | undefined;
  selectedBusinessObject: LMBusinessObject | undefined;
  selectedTemplate: LMFacadeReportTemplate | undefined;

  moduleColor = signal('');
  moduleInitials = signal('');
  showDetails = signal(false);
  moduleCardWidth = signal(0);

  @Input() suggestedBusinessObjects: Array<LMBusinessObject> | undefined;
  @Input() suggestedTemplates: Array<LMFacadeReportTemplate> = [];
  @Input() selectedModule: LMModuleFacade | undefined;
  @Input() state!: LMFacadeExplorerState;

  @Output() openModuleFacadeBC: EventEmitter<LMBusinessObject> = new EventEmitter<LMBusinessObject>();
  @Output() startFromScratch: EventEmitter<string> = new EventEmitter<string>();
  @Output() templateDetails: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(SohoAccordionComponent) accordionComponent: SohoAccordionComponent | undefined;
  @ViewChild(SohoDataGridComponent) facadeDataGrid: SohoDataGridComponent | undefined;

  private ro: ResizeObserver | undefined;
  private moduleContent: Element | undefined;
  private currentWidth = 0;

  private cardMargin = 20;

  constructor(
    protected lmThemeService: LMThemeService,
    protected lmFacadeService: LMFacadeService,
    private element: ElementRef
  ) {  }

  ngOnInit() {
    this.initializeModuleStyle();
    this.buildModuleGrid();
    this.moduleContent = this.element.nativeElement.querySelector('.lm-module-content');

    if (this.moduleContent) {

      this.ro = new ResizeObserver(() => {
        if (this.moduleContent) {
          const newWidth = this.moduleContent.clientWidth;
          if (newWidth !== this.currentWidth) {
            this.currentWidth = newWidth;
            const newCardWidth = ((this.currentWidth - this.cardMargin)/2) - 1;
            this.moduleCardWidth.set(newCardWidth);
          }
        }
      });

      this.ro.observe(this.moduleContent);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedModule) {
      setTimeout(() => this.initialModuleAccordion());
      this.initializeModuleStyle();
      this.flattenSelectedModule();
    }
  }

  ngOnDestroy() {
    if (this.ro && this.moduleContent) {
      this.ro.unobserve(this.moduleContent);
    }
  }

  public closeFieldsPanel(): void {
    this.templateDetails.emit(false);
    this.showDetails.set(false);
  }

  private initializeModuleStyle() {
    if (this.selectedModule) {
      this.moduleColor.set(this.lmThemeService.getUniqueBackgroundColorClass(this.selectedModule.title));
      this.moduleInitials.set(this.lmFacadeService.buildInitials(this.selectedModule.title));
    }
  }

  private initialModuleAccordion() {
    if (this.accordionComponent) {

      this.accordionComponent?.updated();
      this.accordionComponent.expand('primary');
    }
  }
  private buildModuleGrid() {
    const headerColumns: SohoDataGridColumn[] = [
      {
        id:             'title',
        name:           Soho.Locale.translate('Facade_BusinessObject'),
        field:          'title',
        sortable:       true,
        searchable:     true,
        filterType:     'text',
        singleline:     true,
      },
      {
        id:             'description',
        name:           Soho.Locale.translate('Description'),
        field:          'description',
        sortable:       false,
        searchable:     true,
        filterType:     'text',
        singleline:     false,
      },
    ];

    this.moduleGridOptions = <SohoDataGridOptions>{
      columns:              headerColumns,
      selectable:           'single',
      editable:             false,
      paging:               true,
      showDirty:            false,
      rowHeight:            'short',
      clickToSelect:        true,
      stretchColumn:        'description',
      enableTooltips:       true,
      stickyHeader:         true,
      filterWhenTyping:     false,
      isList:               true,
      filterDisabled:       false,
      showPageSizeSelector: true,
      filterable:           true,
      pagesize:             20
    };
  }

  private flattenSelectedModule() {
    const foundList:string[] = [];
    const data: Array<LMBusinessObject> = [];

    if (this.selectedModule) {
      this.selectedModule?.sections.forEach(section => {
        section.businessObjects.forEach(bo => {
          if (!foundList.includes(bo.businessClass)) {
            foundList.push(bo.businessClass);
            data.push(bo);
          }
        });
      });
    }

    this.moduleGridData = data;
  }

  onBusinessObjectClicked(businessObject: LMBusinessObject) {
    if (businessObject.moduleTag === '_start_') {
      this.openBusinessClassSelectDialog();
    } else {
      this.selectedBusinessObject = businessObject;
      this.selectedTemplate = undefined;
      this.openModuleFacadeBC.emit(this.selectedBusinessObject);
    }
  }

  onTemplateClicked(template: LMFacadeReportTemplate) {
    this.showDetails.set(true);
    this.selectedTemplate = template;
    this.selectedBusinessObject = undefined;
    this.facadeDetailRequest = { type: 'template', detail: { template: template } };
    this.templateDetails.emit(true);
  }

  viewClicked(view: 'grid' | 'tiles'): void {
    this.state.view = view;
  }

  toggleDataGridSearch(): void {
    if (this.facadeDataGrid)
      this.facadeDataGrid.toggleFilterRow();
  }

  onRowClicked(event: SohoDataGridRowClicked) {
    if (this.moduleGridData) {
      const selectedBusinessClassString = event.item.businessClass;
      const selectedBusinessClass = this.moduleGridData.find((temp: LMBusinessObject) => temp.businessClass === selectedBusinessClassString);
      if (selectedBusinessClass)
        this.onBusinessObjectClicked(selectedBusinessClass);
    }
  }

  openBusinessClassSelectDialog(): void {
    this.lmFacadeService.promptPickerDialog('businessClass', Soho.Locale.translate('StartFromScratch'))
      .pipe(first())
        .subscribe({
          next:  (v)  => { this.handleBusinessClassSelect(v); },
          error: (e)  => { throw (e); }
        });
  }

  private handleBusinessClassSelect(result: LMFacadeSelectDialogResult) {
      if (result.status === 0) {
        return; // cancelled
      }

      if (result.target && result.target.length > 0) {
        this.startFromScratch.emit(result.target);
      }
  }
}
